import { Alert, Button, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DetailHeader as ZhDetailHeader, Grid as ZhGrid, Input as ZhInput } from "zhihao-ui-react";

import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";
import { RolePermissionSelector } from "./RolePermissionSelector";
import type { SystemRole } from "./types";
import { loadRole } from "./RoleFormPage";
import "./RoleDetailPage.scss";

export interface RoleDetailPageProps {
    loadRole?: (id: number | string) => Promise<SystemRole>;
    permissions?: string[];
}

/** 角色详情页，沿用旧 Vue 查询接口并保持只读展示。 */
export function RoleDetailPage({ loadRole: load = loadRole, permissions }: RoleDetailPageProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeBanButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canWrite = currentPermissions.includes("ROLE_ROLE_W");
    const editDisabled = permissions ? currentPermissions.includes("ROLE_BUTTON_BAN") : storeBanButton;
    const [role, setRole] = useState<SystemRole | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const id = new URLSearchParams(location.search).get("id");

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void load(id)
            .then(result => {
                if (!disposed) setRole(result);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("角色信息加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, load]);

    return (
        <section aria-label="role-detail-page" className="role-detail-page">
            <header className="role-detail-page__header">
                <ZhDetailHeader title="角色详情" />
                <Space className="operation-fixed-btn" size={12}>
                    <Button onClick={() => void navigate(-1)}>返回</Button>
                    {canWrite ? (
                        <Button
                            disabled={editDisabled}
                            type="primary"
                            onClick={() => void navigate(`/systemMangement/systemManage/role/edit?id=${id || ""}`)}
                        >
                            编辑
                        </Button>
                    ) : null}
                </Space>
            </header>
            {errorMessage ? (
                <Alert className="role-detail-page__error" message={errorMessage} role="alert" type="error" />
            ) : null}
            <div className="section-title role-detail-page__section-title">角色信息</div>
            <ZhGrid className="role-detail-page__basic-grid" cols={2} gap={[32, 0]}>
                <label>
                    <span>
                        <span className="admin-required-mark">*</span>
                        角色名称
                    </span>
                    {/* 为详情文本提供可读语义，同时保留旧版禁用输入框的视觉形态。 */}
                    <span className="sr-only">角色名称: {role?.name || DEFAULT_VALUE}</span>
                    <ZhInput disabled value={role?.name || DEFAULT_VALUE} />
                </label>
                <label>
                    <span>角色说明</span>
                    {/* 为详情文本提供可读语义，同时保留旧版禁用输入框的视觉形态。 */}
                    <span className="sr-only">角色说明: {role?.remark || DEFAULT_VALUE}</span>
                    <ZhInput disabled value={role?.remark || DEFAULT_VALUE} />
                </label>
            </ZhGrid>
            <div className="section-title role-detail-page__section-title role-detail-page__permission-title">
                配置权限
            </div>
            <RolePermissionSelector disabled showSelectAll={false} value={role?.permissions || []} />
        </section>
    );
}

export default RoleDetailPage;
